import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import {
  FaCarSide,
  FaCheck,
  FaClock,
  FaMinus,
  FaPencilAlt,
  FaTrash,
  FaWalking,
} from 'react-icons/fa';
import { FaCircle, FaDrawPolygon, FaGripLines } from 'react-icons/fa6';
import { PiCrosshair } from 'react-icons/pi';
import checkpointDomId from 'features/Responder/features/Tours/features/TourBuilder/utils/checkpointDomId';
import stopLabelFromIndex from 'features/Responder/features/Tours/features/TourBuilder/utils/stopLabelFormIndex';
import PlacesAutocompleteInput from 'features/Responder/features/Tours/features/TourBuilder/components/PlacesAutocompleteInput.jsx';
import ZonePrefill from './components/ZonePrefill';

/**
 * Card for editing a single stop with drag handle, address search, and lat/lng fields.
 * @param {{ stop: any, idx: number, totalStops: number, googleApiKey?: string, onDragStart?: function, onDragOver?: function, onDragEnd?: function, onRemove?: function, onManualChange?: function, onPlaceSelect?: function, onChangeField?: function, isDragging?: boolean, etaSeconds?: number, displayName?: string, legEtaSeconds?: number, deadlineSeconds?: number|null, totalSeconds?: number|null, tripTotalSeconds?: number|null, legLabel?: string, isIndoor?: boolean, shapeMode?: string, onShapeModeChange?: function, mode?: string, onModeChange?: function }} props
 * @returns {JSX.Element}
 */
export default function StopCard({
  stop,
  idx,
  totalStops,
  googleApiKey,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
  onManualChange,
  onPlaceSelect,
  onChangeField,
  isDragging,
  etaSeconds,
  displayName,
  legEtaSeconds,
  deadlineSeconds,
  totalSeconds,
  tripTotalSeconds,
  legLabel,
  onFocus,
  isIndoor = false,
  shapeMode = 'circle',
  onShapeModeChange = () => {},
  mode = 'DRIVING',
  onModeChange = () => {},
  readOnlyZone = false,
}) {
  const currentShapeMode = (shapeMode || '').toLowerCase();
  const currentMode = (mode || '').toUpperCase();
  const aliasValue =
    typeof stop?.label === 'string' ? stop.label : stopLabelFromIndex(Number(idx) || 0);
  const cardDomId = checkpointDomId(stop, idx);
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);
  const isReadOnly = Boolean(readOnlyZone);

  useEffect(() => {
    if (!isEditingName) return;
    if (nameInputRef.current) nameInputRef.current.focus();
  }, [isEditingName]);

  const formatMinutes = (seconds, defaultLabel = '--') => {
    if (!Number.isFinite(seconds)) return defaultLabel;
    const mins = Math.round(seconds / 60);
    if (mins < 1) return '<1 min';
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    return `${hours}h${rem ? ` ${rem}m` : ''}`;
  };

  const etaLabel = formatMinutes(legEtaSeconds ?? etaSeconds, 'ETA --');
  const deadlineLabel = formatMinutes(deadlineSeconds, '--');
  const totalLabel = formatMinutes(totalSeconds, '--');
  const idxNum = Number.isFinite(Number(idx)) ? Number(idx) : 0;
  const derivedLegLabel = (() => {
    const from = idxNum === 0 ? 'Start' : `Checkpoint ${String.fromCharCode(64 + idxNum)}`;
    const to = `Checkpoint ${String.fromCharCode(65 + idxNum)}`;
    return `${from} → ${to}`;
  })();

  const handleFieldChange = (field, value) => {
    if (isReadOnly) return;
    onChangeField(field, value);
  };

  const handleZonePrefillChange = (field, value) => {
    onChangeField(field, value);
  };

  const handleManualChange = (value) => {
    if (isReadOnly) return;
    onManualChange?.(value);
  };

  const handlePlaceSelect = (payload) => {
    if (isReadOnly) return;
    onPlaceSelect?.(payload);
  };

  return (
    <Card
      id={cardDomId}
      style={{
        background: '#1f3653',
        color: '#e6edf3',
      }}
      className={`shadow-sm draggable-card ${isDragging ? 'dragging' : ''}`}
      onDragOver={onDragOver}
    >
      <Card.Body className="p-3 text-white">
        <div className="d-flex justify-content-center mb-2">
          <div
            className="drag-handle d-flex align-items-center justify-content-center text-muted"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            style={{ padding: 5, cursor: 'grab', minWidth: 32, minHeight: 28 }}
            title="Drag to reorder"
          >
            <FaGripLines />
          </div>
        </div>

        <div className="d-flex gap-3 align-items-center flex-wrap">
          <div className="flex-grow-1 d-flex align-items-start gap-2">
            <div>
              <div className="fw-semibold d-flex align-items-center gap-2">
                {isEditingName ? (
                  <div className="position-relative">
                    <Form.Control
                      ref={nameInputRef}
                      className="border-0 responder-input text-white"
                      size="sm"
                      value={aliasValue}
                      onChange={(e) => handleFieldChange('label', e.target.value)}
                      onBlur={() => setIsEditingName(false)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === 'Escape') {
                          event.preventDefault();
                          setIsEditingName(false);
                        }
                      }}
                      placeholder={stopLabelFromIndex(Number(idx) || 0)}
                      style={{ paddingRight: 32, maxWidth: 260 }}
                      readOnly={isReadOnly}
                    />
                    <span
                      role="button"
                      className="text-white position-absolute top-50 translate-middle-y"
                      title="Save checkpoint name"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setIsEditingName(false);
                      }}
                      style={{ right: 10, fontSize: 16, cursor: 'pointer' }}
                    >
                      <FaCheck />
                    </span>
                  </div>
                ) : (
                  <>
                    <span>
                      {displayName ||
                        (idxNum === 0 ? 'Start' : `Checkpoint ${String.fromCharCode(64 + idxNum)}`)}
                    </span>
                    {!isReadOnly && (
                      <span
                        className="text-white cursor-pointer"
                        title="Edit checkpoint name"
                        onClick={() => setIsEditingName(true)}
                        style={{ fontSize: 14 }}
                      >
                        <FaPencilAlt />
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="text-white small d-flex align-items-center gap-2 flex-wrap">
                <div className="d-flex align-items-center gap-1">
                  <span>{legLabel || derivedLegLabel}</span>
                  <span className="text-white-50">•</span>
                  <FaClock />
                  <span>{etaLabel}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div
              className="text-white cursor-pointer"
              title="Focus this checkpoint on map"
              onClick={onFocus}
              style={{ fontSize: 16 }}
            >
              <PiCrosshair />
            </div>
            <FaTrash
              className="text-white cursor-pointer"
              onClick={onRemove}
              title="Remove this checkpoint"
            />
          </div>
        </div>

        <div className="mt-2">
          <ZonePrefill
            onChangeField={handleZonePrefillChange}
            onShapeModeChange={onShapeModeChange}
            selectedZoneId={stop?.zoneId ?? stop?.geometry?.zoneId ?? ''}
            disabled={isReadOnly}
          />

          <hr className="my-3 opacity-50" />
        </div>

        <div className="mt-3">
          <div className="fw-semibold text-uppercase text-white-50 small mb-2">Shape Mode</div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant={currentShapeMode === 'circle' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => !isReadOnly && onShapeModeChange('circle')}
              disabled={isReadOnly}
            >
              <div className="d-flex align-items-center gap-2">
                <FaCircle />
                <span>Circle</span>
              </div>
            </Button>
            <Button
              variant={currentShapeMode === 'polygon' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => !isReadOnly && onShapeModeChange('polygon')}
              disabled={isReadOnly}
            >
              <div className="d-flex align-items-center gap-2">
                <FaDrawPolygon />
                <span>Polygon</span>
              </div>
            </Button>
            <Button
              variant={currentShapeMode === 'crossline' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => !isReadOnly && onShapeModeChange('crossline')}
              disabled={isReadOnly}
            >
              <div className="d-flex align-items-center gap-2">
                <FaMinus />
                <span>Crossline</span>
              </div>
            </Button>
          </div>

          <hr className="my-2 opacity-30" />
        </div>
        <div className="mt-3">
          <div className="fw-semibold text-uppercase text-white-50 small mb-2">Travel mode</div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant={currentMode === 'DRIVING' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => onModeChange('DRIVING')}
            >
              <div className="d-flex align-items-center gap-2">
                <FaCarSide />
                <span>Driving</span>
              </div>
            </Button>
            <Button
              variant={currentMode === 'WALKING' ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => onModeChange('WALKING')}
            >
              <div className="d-flex align-items-center gap-2">
                <FaWalking />
                <span>Walking</span>
              </div>
            </Button>
            {/* <Button
							variant={mode === 'INDOOR' ? 'primary' : 'outline-secondary'}
							size="sm"
							onClick={() => onModeChange('INDOOR')}
						>
							<div className="d-flex align-items-center gap-2">
								<FaBuilding />
								<span>Indoor</span>
							</div>
						</Button> */}
          </div>
        </div>

        <hr className="my-3 opacity-50" />

        <Form.Group className="mt-2">
          <Form.Label className="small text-white mb-1">Address</Form.Label>
          <PlacesAutocompleteInput
            stop={stop}
            placeholder="Search address or place"
            apiKey={googleApiKey}
            onManualChange={handleManualChange}
            onPlaceSelect={handlePlaceSelect}
            disabled={isReadOnly}
          />
          <Row className="mt-2 g-2">
            <Col>
              <Form.Label className="small text-white mb-1">Latitude</Form.Label>
              <Form.Control
                className="border-0 responder-input"
                size="sm"
                value={stop.lat}
                onChange={(e) => handleFieldChange('lat', e.target.value)}
                placeholder="e.g. 30.2672"
                readOnly={isReadOnly}
              />
            </Col>
            <Col>
              <Form.Label className="small text-white mb-1">Longitude</Form.Label>
              <Form.Control
                className="border-0 responder-input"
                size="sm"
                value={stop.lng}
                onChange={(e) => handleFieldChange('lng', e.target.value)}
                placeholder="-97.7431"
                readOnly={isReadOnly}
              />
            </Col>
          </Row>
        </Form.Group>

        <hr className="my-3 opacity-50" />

        <div className="d-flex flex-wrap gap-3">
          <div className="flex-grow-1">
            <div className="fw-semibold small text-uppercase text-white-50 mb-1">Times</div>
            <div className="small">
              <div className="row g-2 align-items-center mb-2">
                <div
                  className="col-6 text-start"
                  title="Leg duration from previous waypoint to this stop"
                >
                  ETA
                </div>
                <div className="col-6 text-end">{etaLabel}</div>
              </div>

              <div className="row g-2 align-items-center mb-2">
                <div className="col-6 text-start" title="Arrival window buffer for this stop">
                  Time Window
                </div>
                <div className="col-6 text-end">
                  <Form.Control
                    type="number"
                    min={0}
                    className="border-0 responder-input text-start"
                    size="sm"
                    value={
                      stop.timeWindowMinutes === '' || stop.timeWindowMinutes == null
                        ? ''
                        : stop.timeWindowMinutes
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      onChangeField('timeWindowMinutes', val === '' ? '' : Number(val));
                    }}
                    placeholder="0"
                    style={{ width: 80, display: 'inline-block' }}
                  />
                </div>
              </div>
              <div className="row g-2 align-items-center mb-2">
                <div className="col-6 text-start text-white-75" title="ETA plus time window buffer">
                  Deadline
                </div>
                <div className="col-6 text-end">{deadlineLabel}</div>
              </div>

              <div className="row g-2 align-items-center mb-2">
                <div className="col-6 text-start" title="Dwell time at this checkpoint">
                  Checkpoint Time
                </div>
                <div className="col-6 text-end">
                  <Form.Control
                    type="number"
                    min={0}
                    className="border-0 responder-input text-start"
                    size="sm"
                    value={
                      stop.stopTimeMinutes === '' || stop.stopTimeMinutes == null
                        ? ''
                        : stop.stopTimeMinutes
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      onChangeField('stopTimeMinutes', val === '' ? '' : Number(val));
                    }}
                    placeholder="0"
                    style={{ width: 80, display: 'inline-block' }}
                  />
                </div>
              </div>
              <div className="row g-2 align-items-center">
                <div
                  className="col-6 text-start text-white-75"
                  title="ETA + time window + stop time"
                >
                  Total
                </div>
                <div className="col-6 text-end">{totalLabel}</div>
              </div>
            </div>
          </div>
        </div>

        {isIndoor ? (
          <>
            <hr className="my-3 opacity-50" />

            <div className="d-flex flex-wrap gap-3">
              <div className="flex-grow-1">
                <div className="fw-semibold small text-uppercase text-white-50 mb-1">
                  Location details
                </div>
                <div className="row g-2 align-items-center mb-2">
                  <div className="col-6 text-start" title="Floor or level">
                    Floor
                  </div>
                  <div className="col-6 text-end">
                    <Form.Control
                      className="border-0 responder-input text-start"
                      size="sm"
                      value={stop.floor ?? ''}
                      onChange={(e) => onChangeField('floor', e.target.value)}
                      placeholder="e.g. 2 or L2"
                      style={{ width: 120, display: 'inline-block' }}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column gap-2 text-white small mt-2">
                  <Form.Check
                    type="checkbox"
                    id={`badge-${stop.id}`}
                    label="Requires badge/access code"
                    checked={!!stop.isBadgeRequired}
                    onChange={(e) => onChangeField('isBadgeRequired', e.target.checked)}
                    className="text-white cursor-default"
                  />
                  <Form.Check
                    type="checkbox"
                    id={`elevator-${stop.id}`}
                    label="Elevator nearby/required"
                    checked={!!stop.hasElevator}
                    onChange={(e) => onChangeField('hasElevator', e.target.checked)}
                    className="text-white cursor-default"
                  />
                  <Form.Check
                    type="checkbox"
                    id={`stairs-${stop.id}`}
                    label="Stair access available"
                    checked={!!stop.hasStairs}
                    onChange={(e) => onChangeField('hasStairs', e.target.checked)}
                    className="text-white cursor-default"
                  />
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="border-0 responder-input text-start mt-1"
                    value={stop.indoorNotes ?? ''}
                    onChange={(e) => onChangeField('indoorNotes', e.target.value)}
                    placeholder="Notes: nearest exit, door code contact, etc."
                  />
                </div>
              </div>
            </div>
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
}
